import React, { useMemo } from 'react';

import { Title } from '../../../components/Title';
import { useOfferContext } from '../../../context';
import { ENewbuildingSnippetType } from '../../../types/offer';
import { isCommercial } from '../../../utils/computedProps/commercial/isCommercial';
import { getOfferName } from '../../../utils/computedProps/title/getOfferName';
import { getOfferSubtitle } from '../../../utils/computedProps/title/getOfferSubtitle';
import { isNewbuildingBookedFromDeveloper } from '../../../utils/newbuildingBooking';
import { addMortgageTypeToUrl, getMortgageType } from '../PriceContainer/utils';
import { CommercialTitleContainer } from '../commercial/CommercialTitleContainer';
import { getTermsValue } from 'shared/json_query/reducers/helpers';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { isCianPartner as isCianPartnerUtil } from 'shared/utils/dailyrent/isCianPartner';

export function TitleContainer() {
  const { offer } = useOfferContext();

  if (isCommercial(offer)) {
    return <CommercialTitleContainer />;
  }

  return <ResidentialTitleContainer />;
}

function ResidentialTitleContainer() {
  const {
    title,
    originalTitle,
    subtitle: offerSubtitle,
    isTop,
    isNewbuildingFromDeveloper,
    offer,
    jsonQuery,
  } = useOfferContext();
  const { fullUrl, externalOfferUrl, newbuilding, dailyrent } = offer;

  const { config } = useApplicationContext();

  const isBookedFromDeveloper = isNewbuildingBookedFromDeveloper(offer);
  const isV2BookedFromDeveloperEnabled = config.get<boolean>('Newbuilding.BookedFromDeveloperDesktop.Enabled');

  const isBookedFromDeveloperAvaliable = Boolean(isBookedFromDeveloper && isV2BookedFromDeveloperEnabled);

  const isCianPartner = isCianPartnerUtil(offer);

  // Получаем выбранные типы ипотеки из фильтров
  const promoSearchTypes = getTermsValue('promo_search_types')(jsonQuery);
  const mortgageType = getMortgageType(promoSearchTypes);

  const rel = isCianPartner ? 'noreferrer nofollow noindex' : 'noreferrer';

  // Формируем href с учетом типа ипотеки
  const href = useMemo(() => {
    const baseUrl = isCianPartner ? externalOfferUrl : fullUrl;

    if (isCianPartner || !mortgageType) {
      return baseUrl;
    }

    return addMortgageTypeToUrl(baseUrl, mortgageType);
  }, [isCianPartner, externalOfferUrl, fullUrl, mortgageType]);

  const subtitle = dailyrent?.hotelData?.isHotel
    ? null
    : getOfferSubtitle({
        isNewbuildingFromDeveloper,
        originalSubtitle: offerSubtitle,
        house: newbuilding?.house,
        hideCompletionDate:
          Boolean(newbuilding?.snippetType === ENewbuildingSnippetType.Maximum) && Boolean(originalTitle),
        ...(newbuilding?.snippetType === ENewbuildingSnippetType.Maximum &&
          originalTitle && { additionalTextBegin: getOfferName(offer) }),
      });

  const starRating = dailyrent?.hotelData?.starRating;

  return (
    <Title
      isTop={isTop}
      title={title}
      subtitle={subtitle}
      starRating={starRating}
      href={href}
      rel={rel}
      isBookedFromDeveloper={isBookedFromDeveloperAvaliable}
    />
  );
}
